import { graphql } from '../gql';
import { useQuery } from '@apollo/client';
import type { ClaimFilterInput, PaginationSort } from '../gql/graphql';

export const GET_CLAIM_AUDIT = graphql(`
  query getClaimAudit($filters: ClaimFilterInput, $auditSort: PaginationSort) {
    claims(filters: $filters) {
      edges {
        node {
          uuid
          audit(sort: $auditSort) {
            edges {
              node {
                tenant
                userEmail
                targetName
                targetUuid
                message
                modifiedProperties
                createdAt
                uuid
              }
            }
          }
        }
      }
    }
  }
`);

///
export interface IUseClaimAuditQueryArg {
  filters?: ClaimFilterInput;
  auditSort?: PaginationSort;
}

// Type definitions for audit data structures
interface LossTypeItem {
  label?: string;
  selected?: boolean;
  children?: LossTypeItem[];
  options?: LossTypeItem[];
}

interface CoverageItem {
  name?: string;
  selected?: boolean;
  sub_coverages?: CoverageItem[];
}

interface CoverageType {
  type?: string;
  coverages?: CoverageItem[];
}

interface AdditionalCoverageItem {
  name?: string;
  uuid?: string;
}

interface AttachmentItem {
  filename?: string;
  name?: string;
  originalName?: string;
  fileName?: string;
  displayName?: string;
  title?: string;
  filePath?: string;
  path?: string;
  url?: string;
  src?: string;
  href?: string;
}

export function useClaimAuditQuery(
  claimUuid: string,
  auditSort?: PaginationSort
) {
  const { loading, data, error } = useQuery(GET_CLAIM_AUDIT, {
    variables: {
      filters: { claimUuid },
      auditSort,
    },
    fetchPolicy: 'no-cache',
  });

  const abstractAuditData = data?.claims?.edges?.flatMap((claim) =>
    claim.node.audit.edges.map((edge) => edge.node)
  );

  const auditData = abstractAuditData?.map((audit, index) => {
    // Convert modifiedProperties to array format
    let description: string[] = [];
    const eventMessage = audit?.message || '';
    const isAddingOrCreating =
      /\b(added|create|created|add|initiate|initiated)\b/i.test(eventMessage);
    const isUpdatingOrModifying = /\b(update|updated|modify|modified)\b/i.test(
      eventMessage
    );

    if (
      audit?.modifiedProperties &&
      (isAddingOrCreating || isUpdatingOrModifying)
    ) {
      if (Array.isArray(audit.modifiedProperties)) {
        description = audit.modifiedProperties;
      } else if (typeof audit.modifiedProperties === 'string') {
        description = [audit.modifiedProperties];
      } else if (typeof audit.modifiedProperties === 'object') {
        // Convert object to array of key-value descriptions
        description = Object.entries(audit.modifiedProperties || {})
          .filter(([key, value]) => {
            // Rule 1: Never show keys containing 'uuid' or 'id'
            if (
              key.toLowerCase().includes('uuid') ||
              key.toLowerCase().includes('id')
            ) {
              return false;
            }

            // Rule 2: For all events, don't show null values
            return value !== null && value !== undefined && value !== '';
          })
          .map(([key, value]) => {
            // Handle addresses and contacts - just show "updated" only for update contact events
            if (
              /\bupdate.*contact\b/i.test(eventMessage) &&
              (key.toLowerCase().includes('address') ||
                key.toLowerCase().includes('contact'))
            ) {
              return `${key.replace(/_/g, ' ')}: updated`;
            }

            // Handle notes - format markdown content properly for note events
            if (
              (/\b(add|update).*note\b/i.test(eventMessage) ||
                /\bnote\b/i.test(eventMessage)) &&
              key.toLowerCase().includes('note') &&
              typeof value === 'string'
            ) {
              const formattedNote = value
                // Remove markdown headers (####, ###, ##, #)
                .replace(/^#{1,6}\s*/gm, '')
                // Remove strikethrough formatting (~~text~~)
                .replace(/~~(.*?)~~/g, '$1')
                // Remove bold formatting (**text** or __text__)
                .replace(/(\*\*|__)(.*?)\1/g, '$2')
                // Remove italic formatting (*text* or _text_)
                .replace(/(\*|_)(.*?)\1/g, '$2')
                // Clean up numbered lists (keep the numbers but clean formatting)
                .replace(/^\s*(\d+)\.\s+/gm, '$1. ')
                // Clean up bullet lists
                .replace(/^\s*[-*+]\s+/gm, '• ')
                // Remove excessive whitespace
                .replace(/\n\s*\n/g, '\n')
                .trim();

              return `${key.replace(/_/g, ' ')}: ${formattedNote}`;
            }

            // Handle different value types
            if (value !== null && typeof value === 'object') {
              if (Array.isArray(value)) {
                // Handle categories array
                if (key === 'categories') {
                  return `${key.replace(/_/g, ' ')}: ${value
                    .map((cat) => {
                      // Remove NoteCategoryType. prefix if present
                      let cleanCategory = cat;
                      if (
                        typeof cat === 'string' &&
                        cat.startsWith('NoteCategoryType.')
                      ) {
                        cleanCategory = cat.replace('NoteCategoryType.', '');
                      }

                      if (cleanCategory === 'CORRESPONDENCE')
                        return 'Correspondence';
                      return cleanCategory
                        .replace(/_/g, ' ')
                        .toLowerCase()
                        .replace(/\b\w/g, (l: string) => l.toUpperCase());
                    })
                    .join(', ')}`;
                }

                // Handle attachments array
                if (key === 'attachments') {
                  const attachmentNames = value.map((att) => {
                    if (att && typeof att === 'object') {
                      // Try different possible properties for the filename
                      if (att.filename) return att.filename;
                      if (att.name) return att.name;
                      if (att.filePath)
                        return att.filePath.split('/').pop() || 'file';
                      if (att.url) {
                        // Extract filename from URL
                        const urlPath = att.url.split('/').pop();
                        return urlPath ? decodeURIComponent(urlPath) : 'file';
                      }
                    }
                    return 'attachment';
                  });
                  return `${key.replace(/_/g, ' ')}: ${attachmentNames.join(
                    ', '
                  )}`;
                }

                // Handle losstype array
                if (key === 'loss_types') {
                  // For update claim events, just show "updated" for loss types
                  if (/\bupdate.*claim\b/i.test(eventMessage)) {
                    return `${key.replace(/_/g, ' ')}: updated`;
                  }
                  // Collect paths with level-aware formatting
                  const collectSelectedPaths = (
                    items: LossTypeItem[]
                  ): string[] => {
                    const allPaths: string[][] = [];

                    const traverse = (
                      item: LossTypeItem,
                      currentPath: string[] = []
                    ): void => {
                      if (item && typeof item === 'object') {
                        const newPath = item.label
                          ? [...currentPath, item.label]
                          : currentPath;

                        // If this item is selected, record the path
                        if (item.selected === true && item.label) {
                          allPaths.push(newPath);
                        }

                        // Continue traversing children
                        const childArrays = [item.children, item.options];
                        for (const childArray of childArrays) {
                          if (Array.isArray(childArray)) {
                            for (const child of childArray) {
                              traverse(child, newPath);
                            }
                          }
                        }
                      }
                    };

                    items.forEach((item) => traverse(item));

                    // Group paths by longest common prefix and format with braces
                    const groupPathsByLongestPrefix = (
                      paths: string[][]
                    ): string[] => {
                      if (paths.length === 0) return [];

                      // Find the longest common prefix for all paths
                      let commonPrefix: string[] = [];
                      if (paths.length > 1) {
                        commonPrefix = paths[0];
                        for (let i = 1; i < paths.length; i++) {
                          const currentPath = paths[i];
                          const newCommon: string[] = [];
                          for (
                            let j = 0;
                            j <
                            Math.min(commonPrefix.length, currentPath.length);
                            j++
                          ) {
                            if (commonPrefix[j] === currentPath[j]) {
                              newCommon.push(commonPrefix[j]);
                            } else {
                              break;
                            }
                          }
                          commonPrefix = newCommon;
                        }
                      } else {
                        // Single path case
                        return [paths[0].join(' > ')];
                      }

                      if (commonPrefix.length === 0) {
                        // No common prefix, just list all paths
                        return paths.map((path) => path.join(' > '));
                      }

                      // Get the remaining parts after the common prefix
                      const remainingParts = paths.map((path) =>
                        path.slice(commonPrefix.length)
                      );

                      // Group remaining parts
                      const groupedParts: string[] = [];

                      for (const remaining of remainingParts) {
                        if (remaining.length === 0) {
                          // This path ends at the common prefix
                          continue;
                        } else if (remaining.length === 1) {
                          // Single remaining item
                          groupedParts.push(remaining[0]);
                        } else {
                          // Multiple levels remaining
                          groupedParts.push(remaining.join(' > '));
                        }
                      }

                      const commonPrefixStr = commonPrefix.join(' > ');

                      if (groupedParts.length === 0) {
                        return [commonPrefixStr];
                      } else if (groupedParts.length === 1) {
                        return [`${commonPrefixStr} > ${groupedParts[0]}`];
                      } else {
                        return [
                          `${commonPrefixStr} > {${groupedParts.join(', ')}}`,
                        ];
                      }
                    };

                    const formattedPaths = groupPathsByLongestPrefix(allPaths);

                    return formattedPaths;
                  };

                  const formattedPaths = collectSelectedPaths(value);

                  if (formattedPaths.length > 0) {
                    return `${key.replace(/_/g, ' ')}: ${formattedPaths.join(
                      ', '
                    )}`;
                  }
                  return null;
                }

                // Handle potential coverages impacted array
                if (
                  key === 'potentialCoveragesImpacted' ||
                  key === 'potential_coverages_impacted' ||
                  key === 'potentialCoverages' ||
                  key === 'potential_coverages'
                ) {
                  // For update claim events, just show "updated" for potential coverages
                  if (/\bupdate.*claim\b/i.test(eventMessage)) {
                    return `${key.replace(/_/g, ' ')}: updated`;
                  }
                  const coverageLabels = [];

                  // Recursive function to find ALL selected paths for coverages structure
                  const findAllSelectedCoveragePaths = (
                    item: CoverageItem,
                    parentPath: string[] = []
                  ): string[][] => {
                    const allPaths: string[][] = [];

                    if (item && typeof item === 'object') {
                      // Build current path with parent path
                      const currentPath = item.name
                        ? [...parentPath, item.name]
                        : parentPath;

                      // Handle sub_coverages array
                      if (Array.isArray(item.sub_coverages)) {
                        for (const subCoverage of item.sub_coverages) {
                          const childPaths = findAllSelectedCoveragePaths(
                            subCoverage,
                            item.selected === true ? currentPath : parentPath
                          );
                          allPaths.push(...childPaths);
                        }
                      }

                      // If this item is selected and has no selected children, add its path
                      if (item.selected === true && item.name) {
                        const hasSelectedChildren =
                          Array.isArray(item.sub_coverages) &&
                          item.sub_coverages.some(
                            (child) => child.selected === true
                          );
                        if (!hasSelectedChildren) {
                          allPaths.push(currentPath);
                        }
                      }
                    }

                    return allPaths;
                  };

                  // Find full path of selected items from all coverage types in the array
                  const allPaths: string[][] = [];
                  for (const coverageType of value) {
                    const typedCoverageType = coverageType as CoverageType;
                    if (
                      typedCoverageType &&
                      typeof typedCoverageType === 'object' &&
                      Array.isArray(typedCoverageType.coverages)
                    ) {
                      const typeLabel = typedCoverageType.type
                        ? typedCoverageType.type.replace(/_/g, ' ')
                        : '';

                      for (const coverage of typedCoverageType.coverages) {
                        const selectedPaths = findAllSelectedCoveragePaths(
                          coverage,
                          typeLabel ? [typeLabel] : []
                        );
                        allPaths.push(...selectedPaths);
                      }
                    }
                  }

                  // Group paths by top-level parent
                  const topLevelGroups = new Map<string, string[][]>();
                  for (const path of allPaths) {
                    if (path.length > 0) {
                      const topLevel = path[0];
                      if (!topLevelGroups.has(topLevel)) {
                        topLevelGroups.set(topLevel, []);
                      }
                      const existing = topLevelGroups.get(topLevel);
                      if (existing) {
                        existing.push(path);
                      }
                    }
                  }

                  // Build coverage labels with hierarchical grouping
                  for (const [topLevel, paths] of topLevelGroups.entries()) {
                    if (paths.length > 0) {
                      // Group by sub-parent within this top level
                      const subGroups = new Map<string, string[]>();

                      for (const path of paths) {
                        if (path.length === 1) {
                          // Direct child of top level
                          const key = '';
                          if (!subGroups.has(key)) {
                            subGroups.set(key, []);
                          }
                          const existing = subGroups.get(key);
                          if (existing) {
                            existing.push(path[0]);
                          }
                        } else if (path.length === 2) {
                          // Two levels: top > child
                          const key = '';
                          if (!subGroups.has(key)) {
                            subGroups.set(key, []);
                          }
                          const existing = subGroups.get(key);
                          if (existing) {
                            existing.push(path[1]);
                          }
                        } else {
                          // Three or more levels: top > sub > children...
                          const subParent = path[1];
                          const childrenPath = path.slice(2);
                          if (!subGroups.has(subParent)) {
                            subGroups.set(subParent, []);
                          }
                          const existing = subGroups.get(subParent);
                          if (existing) {
                            existing.push(childrenPath.join(','));
                          }
                        }
                      }

                      // Format the groups for this top level
                      const formattedSubGroups = [];
                      for (const [subParent, children] of subGroups.entries()) {
                        if (subParent === '') {
                          // Direct children of top level
                          if (children.length === 1) {
                            formattedSubGroups.push(children[0]);
                          } else {
                            formattedSubGroups.push(`{${children.join(',')}}`);
                          }
                        } else {
                          // Sub-hierarchy
                          if (children.length === 1) {
                            formattedSubGroups.push(
                              `{${subParent} > ${children[0]}}`
                            );
                          } else {
                            formattedSubGroups.push(
                              `{${subParent} > ${children.join(',')}}`
                            );
                          }
                        }
                      }

                      if (
                        formattedSubGroups.length === 1 &&
                        !formattedSubGroups[0].includes('{')
                      ) {
                        // Single direct child
                        coverageLabels.push(
                          `${topLevel} : ${formattedSubGroups[0]}`
                        );
                      } else {
                        // Multiple or complex children
                        coverageLabels.push(
                          `${topLevel} : ${formattedSubGroups.join(',')}`
                        );
                      }
                    }
                  }

                  if (coverageLabels.length > 0) {
                    return `${key.replace(/_/g, ' ')}: {${coverageLabels.join(
                      ', '
                    )}}`;
                  }
                  return null;
                }

                // Handle additional coverages impacted array - show all names
                if (key === 'additional_coverages_impacted') {
                  const coverageNames = [];

                  // Extract names from array of coverage objects
                  for (const coverage of value) {
                    const typedCoverage = coverage as AdditionalCoverageItem;
                    if (
                      typedCoverage &&
                      typeof typedCoverage === 'object' &&
                      typedCoverage.name
                    ) {
                      coverageNames.push(typedCoverage.name);
                    }
                  }

                  if (coverageNames.length > 0) {
                    return `${key.replace(/_/g, ' ')}: ${coverageNames.join(
                      ', '
                    )}`;
                  }
                  return null;
                }
              } else {
                // Handle regular objects - check for attachments specifically
                if (
                  key === 'attachments' &&
                  value &&
                  typeof value === 'object'
                ) {
                  // Function to extract filename from attachment
                  const extractFilename = (
                    att: AttachmentItem | string
                  ): string => {
                    // Handle string representation of AttachmentUploadInput
                    if (typeof att === 'string') {
                      // Extract filename from string like "AttachmentUploadInput(file=UploadFile(filename='real_broker_email (1).eml', ..."
                      const filenameMatch = att.match(
                        /filename=['"']([^'"]+)['"']/
                      );
                      if (filenameMatch && filenameMatch[1]) {
                        return filenameMatch[1];
                      }

                      // Fallback: try to find any filename pattern in the string
                      const filenamePattern = /([^/\\]+\.[a-zA-Z0-9]+)/g;
                      const matches = att.match(filenamePattern);
                      if (matches && matches.length > 0) {
                        return matches[0];
                      }
                    }

                    // Handle object
                    if (att && typeof att === 'object') {
                      // Try various possible properties for filename
                      const possibleNames = [
                        att.filename,
                        att.name,
                        att.originalName,
                        att.fileName,
                        att.displayName,
                        att.title,
                      ];

                      for (const name of possibleNames) {
                        if (name && typeof name === 'string' && name.trim()) {
                          return name.trim();
                        }
                      }

                      // Try to extract from path properties
                      const possiblePaths = [
                        att.filePath,
                        att.path,
                        att.url,
                        att.src,
                        att.href,
                      ];

                      for (const path of possiblePaths) {
                        if (path && typeof path === 'string') {
                          const filename = path.split('/').pop();
                          if (filename && filename !== path) {
                            return decodeURIComponent(filename);
                          }
                        }
                      }

                      // If we have any string property that looks like a filename
                      const allValues = Object.values(att).filter(
                        (v) => typeof v === 'string'
                      );
                      for (const val of allValues) {
                        if (
                          val &&
                          typeof val === 'string' &&
                          val.includes('.')
                        ) {
                          const potentialFilename = val.split('/').pop();
                          if (
                            potentialFilename &&
                            potentialFilename.includes('.')
                          ) {
                            return potentialFilename;
                          }
                        }
                      }
                    }

                    return 'attachment';
                  };

                  let attachmentNames = [];

                  // Handle attachments object (like {files: [...]})
                  if ('files' in value && Array.isArray(value.files)) {
                    attachmentNames = value.files.map(extractFilename);
                  }
                  // Handle GraphQL edges structure
                  else if ('edges' in value && Array.isArray(value.edges)) {
                    attachmentNames = value.edges.map((edge) =>
                      extractFilename(edge.node || edge)
                    );
                  }
                  // Handle direct array in object
                  else if (Array.isArray(value)) {
                    attachmentNames = value.map(extractFilename);
                  }
                  // Handle single attachment object
                  else {
                    attachmentNames = [extractFilename(value)];
                  }

                  if (attachmentNames.length > 0) {
                    return `${key.replace(/_/g, ' ')}: ${attachmentNames.join(
                      ', '
                    )}`;
                  }
                }

                // Handle losstype object
                if (
                  key === 'losstype' ||
                  key === 'lossType' ||
                  key === 'lossTypes' ||
                  key === 'loss_type' ||
                  key === 'loss_types'
                ) {
                  // For update claim events, just show "updated" for loss types
                  if (/\bupdate.*claim\b/i.test(eventMessage)) {
                    return `${key.replace(/_/g, ' ')}: updated`;
                  }
                  // Collect paths with level-aware formatting
                  const collectSelectedPathsObj = (
                    item: LossTypeItem
                  ): string[] => {
                    const allPaths: string[][] = [];

                    const traverse = (
                      currentItem: LossTypeItem,
                      currentPath: string[] = []
                    ): void => {
                      if (currentItem && typeof currentItem === 'object') {
                        const newPath = currentItem.label
                          ? [...currentPath, currentItem.label]
                          : currentPath;

                        // If this item is selected, record the path
                        if (
                          currentItem.selected === true &&
                          currentItem.label
                        ) {
                          allPaths.push(newPath);
                        }

                        // Continue traversing children
                        const childArrays = [
                          currentItem.children,
                          currentItem.options,
                        ];
                        for (const childArray of childArrays) {
                          if (Array.isArray(childArray)) {
                            for (const child of childArray) {
                              traverse(child, newPath);
                            }
                          }
                        }
                      }
                    };

                    traverse(item);

                    // Group paths by longest common prefix and format with braces
                    const groupPathsByLongestPrefix = (
                      paths: string[][]
                    ): string[] => {
                      if (paths.length === 0) return [];

                      // Find the longest common prefix for all paths
                      let commonPrefix: string[] = [];
                      if (paths.length > 1) {
                        commonPrefix = paths[0];
                        for (let i = 1; i < paths.length; i++) {
                          const currentPath = paths[i];
                          const newCommon: string[] = [];
                          for (
                            let j = 0;
                            j <
                            Math.min(commonPrefix.length, currentPath.length);
                            j++
                          ) {
                            if (commonPrefix[j] === currentPath[j]) {
                              newCommon.push(commonPrefix[j]);
                            } else {
                              break;
                            }
                          }
                          commonPrefix = newCommon;
                        }
                      } else {
                        // Single path case
                        return [paths[0].join(' > ')];
                      }

                      if (commonPrefix.length === 0) {
                        // No common prefix, just list all paths
                        return paths.map((path) => path.join(' > '));
                      }

                      // Get the remaining parts after the common prefix
                      const remainingParts = paths.map((path) =>
                        path.slice(commonPrefix.length)
                      );

                      // Group remaining parts
                      const groupedParts: string[] = [];

                      for (const remaining of remainingParts) {
                        if (remaining.length === 0) {
                          // This path ends at the common prefix
                          continue;
                        } else if (remaining.length === 1) {
                          // Single remaining item
                          groupedParts.push(remaining[0]);
                        } else {
                          // Multiple levels remaining
                          groupedParts.push(remaining.join(' > '));
                        }
                      }

                      const commonPrefixStr = commonPrefix.join(' > ');

                      if (groupedParts.length === 0) {
                        return [commonPrefixStr];
                      } else if (groupedParts.length === 1) {
                        return [`${commonPrefixStr} > ${groupedParts[0]}`];
                      } else {
                        return [
                          `${commonPrefixStr} > {${groupedParts.join(', ')}}`,
                        ];
                      }
                    };

                    const formattedPaths = groupPathsByLongestPrefix(allPaths);

                    return formattedPaths;
                  };

                  const formattedPaths = collectSelectedPathsObj(value);

                  if (formattedPaths.length > 0) {
                    return `${key.replace(/_/g, ' ')}: ${formattedPaths.join(
                      ', '
                    )}`;
                  }
                  return null;
                }

                // Handle potential coverages impacted object
                if (
                  key === 'potentialCoveragesImpacted' ||
                  key === 'potential_coverages_impacted' ||
                  key === 'potentialCoverages' ||
                  key === 'potential_coverages'
                ) {
                  // For update claim events, just show "updated" for potential coverages
                  if (/\bupdate.*claim\b/i.test(eventMessage)) {
                    return `${key.replace(/_/g, ' ')}: updated`;
                  }
                  const coverageLabels = [];

                  // Recursive function to find ALL selected paths for coverages structure
                  const findAllSelectedCoveragePathsObj = (
                    item: CoverageItem,
                    parentPath: string[] = []
                  ): string[][] => {
                    const allPaths: string[][] = [];

                    if (item && typeof item === 'object') {
                      // Build current path with parent path
                      const currentPath = item.name
                        ? [...parentPath, item.name]
                        : parentPath;

                      // Handle sub_coverages array
                      if (Array.isArray(item.sub_coverages)) {
                        for (const subCoverage of item.sub_coverages) {
                          const childPaths = findAllSelectedCoveragePathsObj(
                            subCoverage,
                            item.selected === true ? currentPath : parentPath
                          );
                          allPaths.push(...childPaths);
                        }
                      }

                      // If this item is selected and has no selected children, add its path
                      if (item.selected === true && item.name) {
                        const hasSelectedChildren =
                          Array.isArray(item.sub_coverages) &&
                          item.sub_coverages.some(
                            (child) => child.selected === true
                          );
                        if (!hasSelectedChildren) {
                          allPaths.push(currentPath);
                        }
                      }
                    }

                    return allPaths;
                  };

                  // Handle single coverage type object
                  const allPaths: string[][] = [];
                  const typedValue = value as CoverageType;
                  if (
                    typedValue &&
                    typeof typedValue === 'object' &&
                    Array.isArray(typedValue.coverages)
                  ) {
                    const typeLabel = typedValue.type
                      ? typedValue.type.replace(/_/g, ' ')
                      : '';

                    for (const coverage of typedValue.coverages) {
                      const selectedPaths = findAllSelectedCoveragePathsObj(
                        coverage,
                        typeLabel ? [typeLabel] : []
                      );
                      allPaths.push(...selectedPaths);
                    }
                  } else {
                    // Handle direct coverage object
                    const selectedPaths = findAllSelectedCoveragePathsObj(
                      value,
                      []
                    );
                    allPaths.push(...selectedPaths);
                  }

                  // Group paths by top-level parent
                  const topLevelGroups = new Map<string, string[][]>();
                  for (const path of allPaths) {
                    if (path.length > 0) {
                      const topLevel = path[0];
                      if (!topLevelGroups.has(topLevel)) {
                        topLevelGroups.set(topLevel, []);
                      }
                      const existing = topLevelGroups.get(topLevel);
                      if (existing) {
                        existing.push(path);
                      }
                    }
                  }

                  // Build coverage labels with hierarchical grouping
                  for (const [topLevel, paths] of topLevelGroups.entries()) {
                    if (paths.length > 0) {
                      // Group by sub-parent within this top level
                      const subGroups = new Map<string, string[]>();

                      for (const path of paths) {
                        if (path.length === 1) {
                          // Direct child of top level
                          const key = '';
                          if (!subGroups.has(key)) {
                            subGroups.set(key, []);
                          }
                          const existing = subGroups.get(key);
                          if (existing) {
                            existing.push(path[0]);
                          }
                        } else if (path.length === 2) {
                          // Two levels: top > child
                          const key = '';
                          if (!subGroups.has(key)) {
                            subGroups.set(key, []);
                          }
                          const existing = subGroups.get(key);
                          if (existing) {
                            existing.push(path[1]);
                          }
                        } else {
                          // Three or more levels: top > sub > children...
                          const subParent = path[1];
                          const childrenPath = path.slice(2);
                          if (!subGroups.has(subParent)) {
                            subGroups.set(subParent, []);
                          }
                          const existing = subGroups.get(subParent);
                          if (existing) {
                            existing.push(childrenPath.join(','));
                          }
                        }
                      }

                      // Format the groups for this top level
                      const formattedSubGroups = [];
                      for (const [subParent, children] of subGroups.entries()) {
                        if (subParent === '') {
                          // Direct children of top level
                          if (children.length === 1) {
                            formattedSubGroups.push(children[0]);
                          } else {
                            formattedSubGroups.push(`{${children.join(',')}}`);
                          }
                        } else {
                          // Sub-hierarchy
                          if (children.length === 1) {
                            formattedSubGroups.push(
                              `{${subParent} > ${children[0]}}`
                            );
                          } else {
                            formattedSubGroups.push(
                              `{${subParent} > ${children.join(',')}}`
                            );
                          }
                        }
                      }

                      if (
                        formattedSubGroups.length === 1 &&
                        !formattedSubGroups[0].includes('{')
                      ) {
                        // Single direct child
                        coverageLabels.push(
                          `${topLevel} : ${formattedSubGroups[0]}`
                        );
                      } else {
                        // Multiple or complex children
                        coverageLabels.push(
                          `${topLevel} : ${formattedSubGroups.join(',')}`
                        );
                      }
                    }
                  }

                  if (coverageLabels.length > 0) {
                    return `${key.replace(/_/g, ' ')}: {${coverageLabels.join(
                      ', '
                    )}}`;
                  }
                  return null;
                }

                // Handle additional coverages impacted object - show all names
                if (
                  key === 'additionalCoveragesImpacted' ||
                  key === 'additional_coverages_impacted' ||
                  key === 'additionalCoverages' ||
                  key === 'additional_coverages'
                ) {
                  const coverageNames = [];

                  // Handle single coverage object
                  const typedValue = value as AdditionalCoverageItem;
                  if (
                    typedValue &&
                    typeof typedValue === 'object' &&
                    typedValue.name
                  ) {
                    coverageNames.push(typedValue.name);
                  }

                  if (coverageNames.length > 0) {
                    return `${key.replace(/_/g, ' ')}: ${coverageNames.join(
                      ', '
                    )}`;
                  }
                  return null;
                }

                // Handle regular objects
                const objectString = Object.entries(value)
                  .filter(
                    ([objKey]) =>
                      !objKey.toLowerCase().includes('uuid') &&
                      !objKey.toLowerCase().includes('id')
                  )
                  .map(([objKey, objValue]) => `${objKey}: ${objValue}`)
                  .join(', ');
                const formattedKey = key.replace(/_/g, ' ');
                return `${formattedKey}: {${objectString}}`;
              }
            }

            // Handle categories specifically (for string values)
            if (key === 'categories' && typeof value === 'string') {
              // Remove NoteCategoryType. prefix if present
              let cleanCategory = value;
              if (value.startsWith('NoteCategoryType.')) {
                cleanCategory = value.replace('NoteCategoryType.', '');
              }

              if (cleanCategory === 'CORRESPONDENCE') {
                return `${key.replace(/_/g, ' ')}: Correspondence`;
              }

              return `${key.replace(/_/g, ' ')}: ${cleanCategory
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase())}`;
            }

            const formattedKey = key.replace(/_/g, ' ');
            return `${formattedKey}: ${value}`;
          })
          .filter((item): item is string => item !== null);
      }
    }

    return {
      id: index,
      date: audit?.createdAt ?? '',
      event: audit?.message,
      userName: audit?.userEmail,
      description,
    };
  });

  return {
    loading,
    error,
    auditData,
    data,
  };
}
